import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { Moment } from "moment";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const WORK_LOG_TIMEZONE = "Asia/Tokyo";

export const nowInTokyo = (): Dayjs => dayjs().tz(WORK_LOG_TIMEZONE);

export const tokyoNowPickerDayjs = (): Dayjs => {
  const t = nowInTokyo();
  return dayjs.tz(
    t.format("YYYY-MM-DD HH:mm"),
    "YYYY-MM-DD HH:mm",
    WORK_LOG_TIMEZONE,
  );
};

export const hasLoggedAtInput = (value: unknown): boolean => {
  if (value == null || value === "") return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (dayjs.isDayjs(value)) return value.isValid();
  return false;
};

export const parseYmdInTokyo = (
  value: string,
  format = "YYYY-MM-DD",
): Dayjs | null => {
  const normalized = value.trim().replace(/\//g, "-");
  const d = dayjs.tz(normalized, format, WORK_LOG_TIMEZONE);
  return d.isValid() ? d : null;
};

export const parseDateDefaultInTokyo = (value?: string): Dayjs => {
  if (!value?.trim()) return nowInTokyo();
  const normalized = value.trim().replace(/-/g, "/");
  const d = dayjs.tz(normalized, "YYYY/MM/DD", WORK_LOG_TIMEZONE);
  return d.isValid() ? d : nowInTokyo();
};

export const momentWallClockAsTokyo = (m: Moment): Dayjs =>
  dayjs.tz(
    m.format("YYYY-MM-DD HH:mm:ss"),
    "YYYY-MM-DD HH:mm:ss",
    WORK_LOG_TIMEZONE,
  );

export const formatIsoInTokyo = (
  iso: string | null | undefined,
  format: string,
): string => {
  if (iso == null || iso === "") return "";
  const d = dayjs(iso).tz(WORK_LOG_TIMEZONE);
  return d.isValid() ? d.format(format) : String(iso).trim();
};

export const isoToTokyoPickerDayjs = (iso: string): Dayjs | null => {
  const t = dayjs(iso).tz(WORK_LOG_TIMEZONE);
  if (!t.isValid()) return null;
  return dayjs.tz(
    t.format("YYYY-MM-DD HH:mm"),
    "YYYY-MM-DD HH:mm",
    WORK_LOG_TIMEZONE,
  );
};

export const isPastWorkDate = (value: string): boolean => {
  const workDate = parseYmdInTokyo(value.trim().replace(/\//g, "-"));
  if (!workDate) return false;
  return workDate.format("YYYY-MM-DD") < nowInTokyo().format("YYYY-MM-DD");
};

export const isTodayWorkDate = (value: string): boolean => {
  const workDate = parseYmdInTokyo(value.trim().replace(/\//g, "-"));
  if (!workDate) return false;
  return workDate.format("YYYY-MM-DD") === nowInTokyo().format("YYYY-MM-DD");
};

export const pickerTimeToLoggedAtIso = (
  picker: Dayjs,
  options: {
    readonlyIso?: string | null;
    workDate?: string;
  },
): string => {
  const { readonlyIso, workDate } = options;
  let dateStr: string;
  if (readonlyIso && dayjs(readonlyIso).isValid()) {
    dateStr = dayjs(readonlyIso).tz(WORK_LOG_TIMEZONE).format("YYYY-MM-DD");
  } else if (workDate) {
    const wd = parseYmdInTokyo(workDate.replace(/\//g, "-"), "YYYY-MM-DD");
    dateStr = wd?.format("YYYY-MM-DD") ?? workDate.trim();
  } else {
    dateStr = nowInTokyo().format("YYYY-MM-DD");
  }
  return dayjs
    .tz(
      `${dateStr} ${picker.format("HH:mm")}`,
      "YYYY-MM-DD HH:mm",
      WORK_LOG_TIMEZONE,
    )
    .format("YYYY-MM-DDTHH:mmZ");
};

export const workDateTimeToLoggedAtIso = (
  workDate: string,
  time: string,
): string | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
    workDate.trim().replace(/\//g, "-"),
  );
  const timeMatch = /^(\d{1,2}):([0-5]\d)$/.exec(time.trim());
  if (!dateMatch || !timeMatch) return null;

  const [, yearText, monthText, dayText] = dateMatch;
  const [, hourText, minuteText] = timeMatch;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hours = Number(hourText);

  const normalizedDate = new Date(0);
  normalizedDate.setUTCHours(0, 0, 0, 0);
  normalizedDate.setUTCFullYear(year, month - 1, day);

  const isValidDate =
    year > 0 &&
    normalizedDate.getUTCFullYear() === year &&
    normalizedDate.getUTCMonth() === month - 1 &&
    normalizedDate.getUTCDate() === day;
  if (!isValidDate) return null;

  normalizedDate.setUTCDate(
    normalizedDate.getUTCDate() + Math.floor(hours / 24),
  );

  const normalizedYear = String(normalizedDate.getUTCFullYear()).padStart(
    4,
    "0",
  );
  const normalizedMonth = String(normalizedDate.getUTCMonth() + 1).padStart(
    2,
    "0",
  );
  const normalizedDay = String(normalizedDate.getUTCDate()).padStart(2, "0");
  const normalizedHours = String(hours % 24).padStart(2, "0");

  const value = dayjs.tz(
    `${normalizedYear}-${normalizedMonth}-${normalizedDay} ${normalizedHours}:${minuteText}`,
    "YYYY-MM-DD HH:mm",
    WORK_LOG_TIMEZONE,
  );
  return value.isValid() ? value.format("YYYY-MM-DDTHH:mm:ssZ") : null;
};
